import { DropdownButtonType,List, ListItem } from '@tableau/widgets';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import * as React from 'react';

import { incrementRouterSpaceReactKey } from '../../app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import { getAppStore } from '../../app/AppStore';
import { Messages } from '../../codegen/Localize';
import { FormikSingleSelectDropdown } from '../../commonInfra/components/FormikSingleSelectDropdown';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { IServerUserInfo } from '../../ts/models/IServerUserInfo';
import { LanguagesInstance } from '../../ts/resources/Languages';
import { ILanguage } from '../../ts/ServerApi/types/ILanguage';
import { UserSettingsNotification,UserSettingsNotificationParams, UserSettingsNotificationType } from '../notifications/UserSettingsNotification';
import { UserSettings } from '../services/resources/UserSettings';
import { UserSettingsStyle } from '../styles/UserSettingsStyle';
import { InlineCancelAndSaveButtons } from './UserSettingEditButtons';

export interface LanguageFormFields {
  language: string;
}

export type LanguageFormProps = {
  initialValues: LanguageFormFields;
  serverUser: IServerUserInfo;
}

const userIsViewingTheirOwnSettings = (sessionManagementService: SessionManagementService, serverUser: IServerUserInfo) => {
  return sessionManagementService.sessionUsername() === serverUser.username
    && sessionManagementService.sessionUserDomainName() === serverUser.domainName;
};

const notifyLanguageChange = (result: IResult, username: string) => {
  const userSettingsNotificationParams: UserSettingsNotificationParams = {
    result: result,
    username: username,
    userSettingsNotificationType: UserSettingsNotificationType.ChangeLanguage
  };
  UserSettingsNotification.notify(userSettingsNotificationParams);
};

async function showLanguageNotification(result: IResult, serverUser: IServerUserInfo) {
  const sessionManagementService = getSessionManagementServiceInstance();
  if (result && userIsViewingTheirOwnSettings(sessionManagementService, serverUser)) {
    await sessionManagementService.refreshSessionInfo();
    getAppStore().dispatch(incrementRouterSpaceReactKey());
    notifyLanguageChange(result, serverUser.username);
  } else if (result) {
    notifyLanguageChange(result, serverUser.username);
  }
}

function showLanguageButton(formik: FormikProps<LanguageFormFields>, initialLanguage: string): boolean {
  if (!formik.dirty || !formik.isValid || formik.isSubmitting || (formik.values.language.trim() === initialLanguage)) {
    return false;
  }
  return true;
}

const unspecifiedLanguageItemValue = 'UnspecifiedLanguage';
export function LanguageForm(props: LanguageFormProps): JSX.Element {
  const [items, setItems] = React.useState([]);
  const [usersCurrentLanguage, setUsersCurrentLanguage] = React.useState('');

  React.useEffect(() => {
    async function fetchLanguages() {
      let languages: ILanguage[] = await LanguagesInstance.getAll();
      languages.unshift(LanguagesInstance.unspecifiedLanguage());
      const newItems: ListItem[] = languages.map((language: ILanguage) => ({
        content: language.displayName,
        value: language.language ? language.language : unspecifiedLanguageItemValue,
        testId: language.language ? language.language : unspecifiedLanguageItemValue,
      }));
      setItems(newItems);
      const usersCurrentLanguage = props.initialValues.language ? props.initialValues.language : unspecifiedLanguageItemValue;
      setUsersCurrentLanguage(usersCurrentLanguage);
    }
    if (items.length === 0) {
      fetchLanguages();
    }
  });

  const handleSubmit = React.useCallback(
    async (language: string, formik: FormikHelpers<LanguageFormFields>) => {
      if (language !== usersCurrentLanguage) {
        let languageToUpdate = language;
        if (language === unspecifiedLanguageItemValue) {
          languageToUpdate = null;
        }
        const result: IResult = await UserSettings.updateUserLanguage(props.serverUser.id, languageToUpdate);
        showLanguageNotification(result, props.serverUser);
        if (!result || result.errors) {
          formik.resetForm();
        } else {
          setUsersCurrentLanguage(language);
        }
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props, setUsersCurrentLanguage]
  );

  return (
    <Formik
      initialValues={{ language: usersCurrentLanguage }}
      onSubmit={function(values: LanguageFormFields, formik: FormikHelpers<LanguageFormFields>) {
        handleSubmit(values.language, formik);
      }}
      enableReinitialize={true}
    >
      {(formik: FormikProps<LanguageFormFields>) => (
        <form onSubmit={formik.handleSubmit}>
          <table>
            <tbody className={UserSettingsStyle.tbody}>
              {/* Language Section */}
              <tr data-test-id="language-section">
              <td className={UserSettingsStyle.labelText}>
                  {Messages.language()}
              </td>
              <td className={UserSettingsStyle.detailsValueSmallPadding}>
                  <FormikSingleSelectDropdown
                      name="language"
                      list={<List items={items} className={UserSettingsStyle.dropdownList}/>}
                      type={DropdownButtonType.Outline}
                      label={''}
                      className={UserSettingsStyle.dropdown}
                      testId={'language'}/>
              </td>
              </tr>
              {(showLanguageButton(formik, usersCurrentLanguage) &&
                <InlineCancelAndSaveButtons
                testId={'edit-language'}
                formik={formik}
                />
              )}
            </tbody>
          </table>
          <div>
          </div>
        </form>
      )}
    </Formik>
  );
}
